import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import {
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
  Send as SendIcon,
} from '@mui/icons-material'

import { EmojiStickerPanel } from './EmojiStickerPanel'
import { ACCEPTED_FILE_TYPES } from './useChatComposer'
import type { ChatComposerController } from './useChatComposer'
import { storagePublicUrl } from '@/utils/storage'

interface ChatComposerProps {
  composer: ChatComposerController
  /** Пояснение, почему писать нельзя. По умолчанию — общий текст read-only. */
  readOnlyHint?: string
  /** Узкая колонка: те же отступы, что у ленты в dense-режиме. */
  dense?: boolean
}

/**
 * Композер одной комнаты: вложения, текст, emoji и стикеры.
 *
 * Только вёрстка — состояние и запросы живут в {@link useChatComposer}, потому
 * что режим правки начинается из ленты, а не отсюда.
 */
export function ChatComposer({ composer, readOnlyHint, dense = false }: ChatComposerProps) {
  const { t } = useTranslation()
  const [attachMenuAnchor, setAttachMenuAnchor] = useState<HTMLElement | null>(null)

  const {
    roomId,
    canWrite,
    text,
    setText,
    pendingAttachments,
    removeAttachment,
    editingId,
    cancelEdit,
    isUploading,
    textInputRef,
    fileInputRef,
    handleFilePick,
    handlePaste,
    insertEmoji,
    send,
    sendSticker,
  } = composer

  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider', p: dense ? 1 : 1.5 }}>
      {!canWrite && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {readOnlyHint ?? t('chat.readOnly')}
        </Alert>
      )}

      {pendingAttachments.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          {pendingAttachments.map((a) => (
            <Box key={a.path} sx={{ position: 'relative' }}>
              {a.type === 'image' ? (
                <img
                  src={storagePublicUrl(a.path)}
                  alt={a.name}
                  style={{ height: 56, borderRadius: 6, objectFit: 'cover' }}
                />
              ) : (
                <Chip icon={<FileIcon />} label={a.name} size="small" />
              )}
              <IconButton
                size="small"
                sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                onClick={() => removeAttachment(a.path)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_FILE_TYPES}
          multiple
          hidden
          disabled={!canWrite}
          onChange={handleFilePick}
        />
        <IconButton
          aria-label={t('chat.attachFile')}
          onClick={(event) => setAttachMenuAnchor(event.currentTarget)}
          disabled={!canWrite || isUploading}
        >
          {isUploading ? <CircularProgress size={20} /> : <AddIcon />}
        </IconButton>
        <Menu
          anchorEl={attachMenuAnchor}
          open={Boolean(attachMenuAnchor)}
          onClose={() => setAttachMenuAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <MenuItem
            onClick={() => {
              setAttachMenuAnchor(null)
              fileInputRef.current?.click()
            }}
          >
            <ListItemIcon>
              <AttachFileIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('chat.attachFile')}</ListItemText>
          </MenuItem>
        </Menu>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          inputRef={textInputRef}
          // Причина недоступности — в Alert над полем: длинная фраза в узком
          // placeholder обрезалась бы на середине слова, а общий текст про
          // договор в чате урока был бы ещё и неверным.
          placeholder={canWrite ? t('chat.messagePlaceholder') : t('chat.readOnlyPlaceholder')}
          value={text}
          disabled={!canWrite}
          onChange={(e) => setText(e.target.value)}
          onPaste={handlePaste}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && canWrite) {
              e.preventDefault()
              void send()
            }
          }}
        />
        <EmojiStickerPanel
          disabled={!canWrite}
          roomId={roomId}
          onPickEmoji={insertEmoji}
          onSendSticker={(stickerId) => void sendSticker(stickerId)}
        />
        <IconButton color="primary" onClick={() => void send()} disabled={!canWrite || isUploading}>
          <SendIcon />
        </IconButton>
      </Box>

      {editingId && (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {t('chat.editingHint')}{' '}
            <Box
              component="span"
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={cancelEdit}
            >
              {t('chat.cancel')}
            </Box>
          </Typography>
        </Box>
      )}
    </Box>
  )
}
